import { Activity, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'

import PermissionMatrix from './PermissionMatrix'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { usePageTitle } from '../../context/PageTitleContext'

const EditRolePage = () => {
  const { uuid } = useParams()
  const { setBreadcrumbs } = usePageTitle()
  const [roleData, setRoleData] = useState({
    name: '',
    description: '',
    permissions: [],
  })
  const [originalRole, setOriginalRole] = useState(null)
  const [loading, setLoading] = useState(true)

  const [permissionOptions, setPermissionOptions] = useState({
    resources: [],
    actions: [],
    scopes: [],
  })

  const [errors, setErrors] = useState({})
  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const roleResponse = await fetchWithAuth(`roles/${uuid}`)

        if (roleResponse && roleResponse.data) {
          const role = roleResponse.data.data || roleResponse.data
          setOriginalRole(role)
          setRoleData({
            name: role.name,
            description: role.description || '',
            permissions: role.permissions || [],
          })
          setBreadcrumbs(['roles', role.name, 'edit'])
        }

        const permissionsResponse = await fetchWithAuth(
          'roles/permission-options',
        )
        if (
          permissionsResponse &&
          permissionsResponse.data &&
          permissionsResponse.data.data
        ) {
          setPermissionOptions(permissionsResponse.data.data)
        }
      } catch (error) {
        console.error('Error fetching data:', error)
        setErrors({ general: 'Failed to load role data. Please try again.' })
      } finally {
        setLoading(false)
      }
    }

    fetchData()
    return () => setBreadcrumbs([])
  }, [uuid])

  const validate = () => {
    const newErrors = {}

    if (roleData.description && roleData.description.length > 200) {
      newErrors.description = 'Description cannot exceed 200 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    setRoleData({
      ...roleData,
      [e.target.name]: e.target.value,
    })

    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: undefined,
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    try {
      const dataToUpdate = originalRole?.isSystem
        ? { description: roleData.description }
        : {
            description: roleData.description,
            permissions: roleData.permissions.map(
              ({ resource, action, scope }) => ({ resource, action, scope }),
            ),
          }

      const response = await fetchWithAuth(`roles/${uuid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToUpdate),
      })

      if (response && response.status === 200) {
        navigate(`/roles/${uuid}`)
      } else {
        const data = response.data
        if (data && data.errors) {
          const apiErrors = {}
          data.errors.forEach((err) => {
            apiErrors[err.field] = err.message
          })
          setErrors(apiErrors)
        } else if (data && data.message) {
          setErrors({ general: data.message })
        }
      }
    } catch (error) {
      console.error('Error updating role:', error)
      setErrors({ general: 'Failed to update role. Please try again.' })
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Paper sx={{ p: 3, mx: 2, my: 4 }}>
      <Typography variant="h6" component="h1" gutterBottom>
        Edit Role: {originalRole?.name}
      </Typography>

      <Activity mode={errors.general ? 'visible' : 'hidden'}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.general}
        </Alert>
      </Activity>

      <Activity mode={originalRole?.isSystem ? 'visible' : 'hidden'}>
        <Alert severity="info" sx={{ mb: 2 }}>
          This is a system role. Only the description can be modified.
        </Alert>
      </Activity>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            label="Name"
            name="name"
            value={roleData.name}
            onChange={handleChange}
            fullWidth
            disabled={true}
            InputProps={{
              readOnly: true,
            }}
          />

          <TextField
            label="Description"
            name="description"
            value={roleData.description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={2}
            error={!!errors.description}
            helperText={errors.description}
            inputProps={{ maxLength: 200 }}
          />

          <Activity mode={!originalRole?.isSystem ? 'visible' : 'hidden'}>
            <Stack spacing={2}>
              <Typography variant="h6" component="h2" gutterBottom>
                Permissions
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Define what actions users with this role can perform on
                different resources.
              </Alert>

              <PermissionMatrix
                permissions={roleData.permissions}
                permissionOptions={permissionOptions}
                onChange={(perms) =>
                  setRoleData({ ...roleData, permissions: perms })
                }
              />
            </Stack>
          </Activity>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate(`/roles/${uuid}`)}
            >
              Cancel
            </Button>
            <Button variant="contained" color="primary" type="submit">
              Save Changes
            </Button>
          </Box>
        </Stack>
      </form>
    </Paper>
  )
}

export default EditRolePage
